import { useMemo, useState, useCallback } from "react"
import { toast } from "react-toastify"
import { Col, Row, UncontrolledTooltip, Button, Badge } from "reactstrap"

import TableContainer from "components/Common/TableContainer"
import DeleteModal from "components/Common/DeleteModal"
import GlobalInputSearch from "components/Common/GlobalInputSearch"
import PageWrapper from "components/Common/PageWrapper"

import { useListParams } from "hooks/useListParams"
import { useWorkOrders, useDeleteWorkOrder } from "hooks/useWorkOrder"
import { downloadWorkOrderPdf } from "api/workOrderApi"
import { formatReadableDate } from "utils/formatDate"

import WorkOrderAddEdit from "./WorkOrderAddEdit"
import WorkOrderView from "./WorkOrderView"
import { WorkOrderHeader } from "./WorkOrderHeader"

const WorkOrderList = () => {
  document.title = "Vehicle Work Orders"

  const [modal, setModal] = useState(false)
  const [isEdit, setIsEdit] = useState(false)
  const [selectedData, setSelectedData] = useState(null)
  const [deleteModal, setDeleteModal] = useState(false)
  const [viewId, setViewId] = useState(null)
  const [pdfLoadingId, setPdfLoadingId] = useState(null)

  const { params, setPage, setSearch, resetParams, hasActiveFilters } =
    useListParams({ page: 1, search: "" })

  const queryParams = useMemo(() => {
    const { page = 1, per_page = 10, search, status } = params
    return { page, per_page, search, status }
  }, [params])

  const {
    data: responseData,
    isLoading,
    refetch,
    isRefetching,
  } = useWorkOrders(queryParams)

  const { mutateAsync: deleteItem, isPending: deleteLoading } =
    useDeleteWorkOrder()

  const toggleModal = useCallback(() => {
    setModal(prev => !prev)
    if (modal) {
      setSelectedData(null)
      setIsEdit(false)
    }
  }, [modal])

  const handleAddNewClick = () => {
    setSelectedData(null)
    setIsEdit(false)
    setModal(true)
  }

  const handleEditClick = item => {
    setSelectedData(item)
    setIsEdit(true)
    setModal(true)
  }

  const handleDeleteClick = item => {
    setSelectedData(item)
    setDeleteModal(true)
  }

  const handleDelete = async () => {
    if (!selectedData?.id) return
    try {
      const res = await deleteItem(selectedData.id)
      toast.success(res?.message ?? "Work order deleted successfully.")
      setDeleteModal(false)
      refetch()
    } catch (err) {
      console.error(err)
      toast.error("Failed to delete work order.")
    }
  }

  const statusBadge = status => {
    const map = {
      pending: "warning",
      in_progress: "info",
      completed: "success",
      cancelled: "secondary",
    }
    return (
      <Badge className={`badge-soft-${map[status] || "light"}`}>
        {status?.replace("_", " ") || "-"}
      </Badge>
    )
  }

  const columns = useMemo(
    () => [
      {
        header: "Work Order #",
        accessorKey: "work_order_no",
        cell: ({ row }) => (
          <div className="fw-semibold">{row.original.work_order_no || "-"}</div>
        ),
      },
      {
        header: "Vehicle",
        cell: ({ row }) => {
          const v = row.original.vehicle
          return v ? `${v.stock_id || ""} ${v.brand?.name || ""} ${v.model?.name || ""}`.trim() || "-" : "-"
        },
      },
      {
        header: "Items",
        cell: ({ row }) => row.original.items?.length ?? 0,
      },
      {
        header: "Status",
        accessorKey: "status",
        cell: ({ row }) => statusBadge(row.original.status),
      },
      {
        header: "Assigned",
        accessorKey: "assigned_date",
        cell: ({ row }) =>
          row.original.assigned_date
            ? formatReadableDate(row.original.assigned_date)
            : "-",
      },
      {
        header: "Completed",
        accessorKey: "completed_date",
        cell: ({ row }) =>
          row.original.completed_date
            ? formatReadableDate(row.original.completed_date)
            : "-",
      },
      {
        header: "Action",
        cell: ({ row }) => {
          const d = row.original
          return (
            <div className="d-flex gap-1">
              <Button
                color="primary"
                size="sm"
                onClick={() => setViewId(d.id)}
                id={`view-${d.id}`}
              >
                <i className="mdi mdi-eye" />
              </Button>
              <UncontrolledTooltip target={`view-${d.id}`}>View</UncontrolledTooltip>

              <Button
                color="success"
                size="sm"
                disabled={pdfLoadingId === d.id}
                onClick={() =>
                  downloadWorkOrderPdf({
                    id: d.id,
                    onStart: () => setPdfLoadingId(d.id),
                    onEnd: () => setPdfLoadingId(null),
                  })
                }
                id={`pdf-${d.id}`}
              >
                <i className={`mdi ${pdfLoadingId === d.id ? "mdi-loading mdi-spin" : "mdi-file-pdf-box"}`} />
              </Button>
              <UncontrolledTooltip target={`pdf-${d.id}`}>
                {pdfLoadingId === d.id ? "Generating..." : "PDF"}
              </UncontrolledTooltip>

              <Button
                color="info"
                size="sm"
                onClick={() => handleEditClick(d)}
                id={`edit-${d.id}`}
              >
                <i className="mdi mdi-pencil-outline" />
              </Button>
              <UncontrolledTooltip target={`edit-${d.id}`}>Edit</UncontrolledTooltip>

              <Button
                color="danger"
                size="sm"
                onClick={() => handleDeleteClick(d)}
                id={`delete-${d.id}`}
              >
                <i className="mdi mdi-delete-outline" />
              </Button>
              <UncontrolledTooltip target={`delete-${d.id}`}>Delete</UncontrolledTooltip>
            </div>
          )
        },
      },
    ],
    [pdfLoadingId]
  )

  const data = responseData?.data?.data || []
  const paginationInfo = responseData?.data

  return (
    <PageWrapper title="Vehicle Work Orders" breadcrumbItem="Work Orders">
      <WorkOrderHeader
        handleAddNewClick={handleAddNewClick}
        handleRefresh={refetch}
        resetParams={resetParams}
        isRefetching={isRefetching}
        hasActiveFilters={hasActiveFilters}
        total={paginationInfo?.total ?? 0}
      />

      <DeleteModal
        show={deleteModal}
        loading={deleteLoading}
        onDeleteClick={handleDelete}
        onCloseClick={() => setDeleteModal(false)}
      />

      <Row className="my-3">
        <Col sm={4}>
          <GlobalInputSearch value={params.search} onChange={setSearch} />
        </Col>
      </Row>

      <TableContainer
        columns={columns}
        data={data}
        isPagination
        paginationInfo={paginationInfo}
        onPageChange={setPage}
        loading={isLoading || isRefetching}
        className="table-responsive"
      />

      <WorkOrderAddEdit
        modal={modal}
        isEdit={isEdit}
        editedData={selectedData}
        toggleModal={toggleModal}
        onSuccess={refetch}
      />

      <WorkOrderView
        workOrderId={viewId}
        isOpen={Boolean(viewId)}
        toggle={() => setViewId(null)}
        onChanged={refetch}
      />
    </PageWrapper>
  )
}

export default WorkOrderList
